import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Mail, Activity, Plus, Trash2, Check, AlertTriangle, Shield, Terminal } from 'lucide-react';
import {
  listSenders, createSender, deleteSender,
  fetchSyncLog, getRequireApproval, setRequireApproval,
} from '../../lib/supabase';
import { PageHeader } from '../../components/PageHeader';
import { Modal } from '../../components/Modal/Modal';
import { formatDateTime } from '../../lib/format';
import { RechteTab } from './RechteTab';
import { PromptTab } from './PromptTab';

type Tab = 'rechte' | 'approval' | 'senders' | 'prompt' | 'sync';

export function Settings() {
  const [tab, setTab] = useState<Tab>('rechte');

  return (
    <div>
      <PageHeader title="Einstellungen" subtitle="Benutzer und Rechte, Freigabe-Workflow, Absender, Prompt für Claude Code und Sync verwalten" />

      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border-color)', overflowX: 'auto' }} data-tour="settings-reiter">
        <TabButton active={tab === 'rechte'}   onClick={() => setTab('rechte')}><Users size={16} /> Benutzer &amp; Rechte</TabButton>
        <TabButton active={tab === 'approval'} onClick={() => setTab('approval')}><Shield size={16} /> Freigabe-Workflow</TabButton>
        <TabButton active={tab === 'senders'}  onClick={() => setTab('senders')}><Mail size={16} /> Absender</TabButton>
        <TabButton active={tab === 'prompt'}   onClick={() => setTab('prompt')}><Terminal size={16} /> Prompt für Claude Code</TabButton>
        <TabButton active={tab === 'sync'}     onClick={() => setTab('sync')}><Activity size={16} /> Sync-Status</TabButton>
      </div>

      {tab === 'rechte'   && <RechteTab />}
      {tab === 'approval' && <ApprovalTab onRechte={() => setTab('rechte')} />}
      {tab === 'senders'  && <SendersTab />}
      {tab === 'prompt'   && <PromptTab />}
      {tab === 'sync'     && <SyncTab />}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem',
      background: 'transparent', border: 'none',
      borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
      marginBottom: '-1px', color: active ? 'var(--primary)' : 'var(--text-secondary)',
      fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </button>
  );
}

function ApprovalTab({ onRechte }: { onRechte: () => void }) {
  const queryClient = useQueryClient();
  const { data: requireApproval, isLoading } = useQuery({
    queryKey: ['require-approval'],
    queryFn: getRequireApproval,
  });

  const toggleMutation = useMutation({
    mutationFn: setRequireApproval,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['require-approval'] }),
  });

  return (
    <div>
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h2 className="card-title" style={{ marginBottom: '0.75rem' }}>So läuft die Freigabe</h2>
        <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
          Mails entstehen im Assistenten („Neue Mail"). Wer nicht selbst versenden darf, reicht sie am Ende zur Freigabe ein;
          die Redaktion prüft sie unter „Freigaben" und plant sie ein. Die Redaktion braucht <strong>vier Werktage</strong>
          (Mo–Fr); eilige Mails, die bis 10 Uhr eingereicht sind, gehen noch am selben Tag raus, sonst am nächsten Werktag —
          immer mit Begründung. Wer das Recht <strong>„ohne Freigabe versenden"</strong> hat, macht am Ende selbst den
          Versand-Check und verschickt; die eigene Mail gibt sonst immer jemand anderes frei.
        </p>
        <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: '0.9rem' }} onClick={onRechte}>
          <Users size={14} aria-hidden="true" /> Freigaberecht und „ohne Freigabe versenden" vergeben
        </button>
      </div>

      <div className="card">
        <h2 className="card-title" style={{ marginBottom: '1rem' }}>Freigabe-Workflow für Kampagnen der Redaktion</h2>
        <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', margin: '0 0 1rem' }}>
          Wenn aktiv, reichen Redakteur:innen ihre Kampagnen aus dem Kampagnen-Editor <strong>zur Freigabe ein</strong>.
          Admins und wer „ohne Freigabe versenden" darf, versenden selbst.
        </p>

        {isLoading ? (
          <div className="loading-spinner" />
        ) : (
          <label style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '1rem', borderRadius: 'var(--radius-md)',
            background: requireApproval ? 'var(--primary-light)' : 'var(--surface-secondary)',
            cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={requireApproval ?? false}
              onChange={(e) => toggleMutation.mutate(e.target.checked)}
              style={{ accentColor: 'var(--primary)', width: 22, height: 22 }}
            />
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {requireApproval ? 'Freigabe ist aktiv' : 'Freigabe ist inaktiv'}
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                {requireApproval
                  ? 'Redakteur:innen reichen Kampagnen zur Freigabe ein. Die Redaktion entscheidet unter „Freigaben".'
                  : 'Redakteur:innen mit „ohne Freigabe versenden" verschicken Kampagnen direkt.'}
              </div>
            </div>
          </label>
        )}
      </div>
    </div>
  );
}

function SendersTab() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', email: '', is_default: false });

  const { data, isLoading } = useQuery({ queryKey: ['senders'], queryFn: listSenders });

  const createMutation = useMutation({
    mutationFn: createSender,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['senders'] });
      setModalOpen(false);
      setNewForm({ name: '', email: '', is_default: false });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSender,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['senders'] }),
  });

  return (
    <div>
      <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
        <AlertTriangle size={16} />
        <div>Absender müssen <strong>in Brevo DKIM/SPF/DMARC verifiziert</strong> werden.</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}><Plus size={16} /> Absender hinzufügen</button>
      </div>

      {isLoading ? (
        <div className="loading-spinner" style={{ margin: '2rem auto' }} />
      ) : !data || data.length === 0 ? (
        <div className="alert alert-info"><Mail size={16} /><div>Noch keine Absender angelegt.</div></div>
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>E-Mail</th><th>Status</th><th>Standard</th><th style={{ textAlign: 'right' }}>Aktion</th></tr>
          </thead>
          <tbody>
            {data.map(s => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{s.email}</td>
                <td>
                  {s.is_verified ? (
                    <span className="status-badge" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}><Check size={11} /> verifiziert</span>
                  ) : (
                    <span className="status-badge" style={{ background: 'var(--warning-light)', color: 'var(--warning-dark)' }}><AlertTriangle size={11} /> nicht verifiziert</span>
                  )}
                </td>
                <td>{s.is_default && <Check size={16} style={{ color: 'var(--success-dark)' }} />}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }}
                    onClick={() => { if (confirm(`Absender "${s.email}" wirklich löschen?`)) deleteMutation.mutate(s.id); }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Absender hinzufügen">
        <div className="form-group">
          <label className="form-label">Anzeigename *</label>
          <input className="form-input" placeholder="z.B. Randori-Pro Newsletter" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">E-Mail-Adresse *</label>
          <input className="form-input" type="email" placeholder="newsletter@randori-pro.de" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={newForm.is_default} onChange={(e) => setNewForm({ ...newForm, is_default: e.target.checked })} style={{ accentColor: 'var(--primary)', width: 18, height: 18 }} />
            Als Standard-Absender setzen
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => createMutation.mutate(newForm)} disabled={!newForm.name || !newForm.email || createMutation.isPending}>Anlegen</button>
        </div>
      </Modal>
    </div>
  );
}

function SyncTab() {
  const { data, isLoading } = useQuery({ queryKey: ['sync-log'], queryFn: () => fetchSyncLog(50) });

  return (
    <div>
      <div className="alert alert-info">
        <Activity size={16} />
        <div>
          Sync-Jobs werden über N8N orchestriert:
          <ul style={{ margin: '0.5rem 0 0 1.25rem' }}>
            <li><strong>03:00 Uhr</strong> — GCloud-CRM &rarr; Supabase (Kontakte)</li>
            <li><strong>04:00 Uhr</strong> — Supabase &rarr; Brevo (Kontakte)</li>
            <li><strong>alle 15 min</strong> — Brevo &rarr; Supabase (Stats-Refresh)</li>
          </ul>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-spinner" style={{ margin: '2rem auto' }} />
      ) : !data || data.length === 0 ? (
        <div className="empty-state">
          <Activity size={48} />
          <h3>Noch keine Sync-Läufe</h3>
          <p>Sync-Protokolle erscheinen hier, sobald N8N-Workflows ausgeführt wurden.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr><th>Typ</th><th>Status</th><th>Datensätze</th><th>Dauer</th><th>Gestartet</th></tr>
          </thead>
          <tbody>
            {data.map(l => (
              <tr key={l.id}>
                <td>{l.sync_type}</td>
                <td>
                  <span className="status-badge" style={{
                    background: l.status === 'success' ? 'var(--success-light)' : l.status === 'partial' ? 'var(--warning-light)' : 'var(--danger-light)',
                    color: l.status === 'success' ? 'var(--success-dark)' : l.status === 'partial' ? 'var(--warning-dark)' : 'var(--danger-dark)',
                  }}>
                    {l.status}
                  </span>
                </td>
                <td style={{ fontSize: '0.875rem' }}>
                  {l.records_ok ?? 0} / {l.records_in ?? 0}
                  {l.records_err ? ` (${l.records_err} Fehler)` : ''}
                </td>
                <td style={{ fontSize: '0.875rem' }}>{l.duration_ms != null ? `${l.duration_ms} ms` : '—'}</td>
                <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{formatDateTime(l.started_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
